/**
 * Thin wrapper around the admin-only AI campaign-draft endpoint
 * exposed by ContactEmailController. Mounted under
 * {@code /api/v1/contact/**}, so the parent SecurityConfiguration
 * rule already gates this on ROLE_ADMIN — no client-side auth
 * check needed beyond rendering the assistant only on admin pages.
 *
 * <p>Unlike the shared {@code postRequest} helper, this module talks
 * to {@code apiClient} directly so it can surface the structured
 * {@code message} the backend returns on failure (rate limit, bad
 * key, parse error, timeout). The assistant UI shows that message
 * verbatim — admin needs to know "OpenAI rate limit, try again"
 * vs "AI drafting is not configured" without guessing.
 */
import axios from 'axios';

/** Mirrors the campaign composer field shape so the assistant can hand a returned draft to the parent form 1:1. */
export interface AiCampaignDraft {
  subject?: string | null;
  preheader?: string | null;
  bodyText?: string | null;
  ctaText?: string | null;
  ctaPath?: string | null;
  fromAddress?: string | null;
  /** One-sentence "why this should convert" the assistant shows beside Apply. */
  rationale?: string | null;
  /** One-sentence note on the conversion play the drafter chose and why it fits. */
  strategy?: string | null;
  /** Conversion play id(s) the drafter ran, e.g. ["founder-personal-trial"]. */
  playsUsed?: string[] | null;
  /** Model identifier (e.g. "gpt-4o"). Echoed for the footer. */
  model?: string | null;
}

export interface AiCampaignDraftStatus {
  enabled: boolean;
  model: string | null;
}

export interface AiDraftRequest {
  prompt: string;
  /** Audience label the parent surface already knows (segment name + size). */
  audienceHint?: string;
  audienceSize?: number;
  /** Current composer state. Present → server tells the model to refine, not regenerate. */
  currentDraft?: {
    subject?: string;
    preheader?: string;
    bodyText?: string;
    ctaText?: string;
    ctaPath?: string;
    fromAddress?: string;
  };
}

/** Read the configured state so the assistant can show "AI drafting not configured" instead of failing on first click. */
export async function getAiDraftStatus(): Promise<AiCampaignDraftStatus> {
  const { apiClient } = await import('../../../services/apiService');
  try {
    const response = await apiClient.get('/api/v1/contact/ai-draft/status');
    return response.data as AiCampaignDraftStatus;
  } catch {
    return { enabled: false, model: null };
  }
}

/**
 * Generate (or refine, when {@code currentDraft} is set) a campaign
 * draft. Throws on any non-200 with the backend's structured
 * message so the assistant can render it inline.
 */
export async function generateAiDraft(req: AiDraftRequest): Promise<AiCampaignDraft> {
  const { apiClient } = await import('../../../services/apiService');
  try {
    const response = await apiClient.post('/api/v1/contact/ai-draft', req);
    return response.data as AiCampaignDraft;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as { message?: string; error?: string } | undefined;
      const msg = data?.message || data?.error || err.message;
      throw new Error(msg || 'AI draft request failed');
    }
    throw err;
  }
}
